import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createCommentVNode as _createCommentVNode, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "scenarios-tab" }
const _hoisted_2 = { class: "section screen-transitions-section" }
const _hoisted_3 = { class: "section-header" }
const _hoisted_4 = { class: "state-buttons-grid" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "state-icon" }
const _hoisted_7 = { class: "state-name" }
const _hoisted_8 = {
  key: 0,
  class: "session-info"
}
const _hoisted_9 = { class: "session-id" }
const _hoisted_10 = { class: "section" }
const _hoisted_11 = {
  key: 0,
  class: "mock-data-grid"
}
const _hoisted_12 = { class: "config-row" }
const _hoisted_13 = { class: "config-row" }
const _hoisted_14 = { class: "config-row" }
const _hoisted_15 = { class: "config-row" }
const _hoisted_16 = { class: "config-select-group" }
const _hoisted_17 = ["onClick"]
const _hoisted_18 = { class: "section" }
const _hoisted_19 = { class: "screen-presets-grid" }
const _hoisted_20 = ["onClick"]
const _hoisted_21 = { class: "preset-icon" }
const _hoisted_22 = { class: "preset-name" }
const _hoisted_23 = { class: "preset-desc" }
const _hoisted_24 = { class: "section" }
const _hoisted_25 = { class: "presets-grid" }
const _hoisted_26 = ["onClick"]
const _hoisted_27 = { class: "preset-icon" }
const _hoisted_28 = { class: "preset-name" }
const _hoisted_29 = { class: "preset-desc" }
const _hoisted_30 = { class: "section" }
const _hoisted_31 = { class: "toggle-list" }
const _hoisted_32 = { class: "switch" }
const _hoisted_33 = ["checked"]
const _hoisted_34 = { class: "switch" }
const _hoisted_35 = ["checked"]
const _hoisted_36 = { class: "switch" }
const _hoisted_37 = ["checked"]
const _hoisted_38 = { class: "section" }
const _hoisted_39 = {
  key: 0,
  class: "config-grid"
}
const _hoisted_40 = { class: "config-row" }
const _hoisted_41 = { class: "config-select-group" }
const _hoisted_42 = ["onClick"]
const _hoisted_43 = { class: "config-row" }
const _hoisted_44 = ["value"]
const _hoisted_45 = { class: "config-row" }
const _hoisted_46 = ["value"]
const _hoisted_47 = { class: "config-row" }
const _hoisted_48 = ["value"]
const _hoisted_49 = { class: "config-row" }
const _hoisted_50 = ["value"]
const _hoisted_51 = { class: "config-row" }
const _hoisted_52 = { class: "config-select-group" }
const _hoisted_53 = ["onClick"]

import { computed, ref, reactive } from "vue";
import { useDevSettings } from "@/composables/useDevSettings";
import { useTestConfig, POSITION_OPTIONS, PMS_OPTIONS } from "@/composables/useTestConfig";
import { useStateMachine, type AIAssistantState, type Template } from "@/composables/useStateMachine";

interface ScreenPreset {
  id: string;
  name: string;
  description: string;
  icon: string;
  targetState: AIAssistantState;
  needsSessionData?: boolean;
  needsTemplates?: boolean;
  needsSummary?: boolean;
}

interface Preset {
  id: string;
  name: string;
  description: string;
  icon: string;
  settings: {
    simulateSocketDisconnect: boolean;
    simulatePollingTimeout: boolean;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'ScenariosTab',
  setup(__props) {

const { settings, updateSetting, resetSettings } = useDevSettings();
const { config: testConfig, updateConfig, resetConfig: resetTestConfig } = useTestConfig();

// AI Assistant State Machine (singleton)
const machine = useStateMachine();

// Current state from State Machine
const currentState = computed(() => machine.state);

// State options for quick transitions (matching useStateMachine states)
const stateOptions: { id: AIAssistantState; label: string; icon: string }[] = [
  { id: "idle", label: "Idle", icon: "⏸️" },
  { id: "form_template_selection", label: "Form Template", icon: "📋" },
  { id: "device_selection", label: "Device", icon: "📱" },
  { id: "recording", label: "Record", icon: "🎙️" },
  { id: "processing", label: "Process", icon: "⏳" },
  { id: "review_ready", label: "Review", icon: "✅" },
  { id: "error", label: "Error", icon: "❌" },
];

// Mock data state
const mockData = reactive({
  sessionId: "dev-session-" + Date.now().toString(36),
  recorderSessionId: "rec-" + Date.now().toString(36),
  recorderUrl: "https://recorder.noa.ai/dev-test",
  licenseId: "123",
  patientId: "456",
  templateCount: 3,
  summaryData:
    "## Anamnese\n\nPaciente apresenta queixa de dor de cabeça há 3 dias.\n\n## Exame Físico\n\nPA: 120/80 mmHg\nFC: 72 bpm\n\n## Conduta\n\nPrescrito analgésico.",
});

// Generate mock templates
const generateMockTemplates = (count: number): Template[] => {
  const templateNames = ["Consulta Geral", "Retorno", "Primeira Consulta", "Exame Físico", "Acompanhamento"];
  return Array.from({ length: count }, (_, i) => ({
    id: i + 1,
    description: templateNames[i] || `Template ${i + 1}`,
    sections: [],
  }));
};

// Screen presets for quick testing (using new flat state machine states)
const screenPresets: ScreenPreset[] = [
  {
    id: "processing",
    name: "Processing",
    description: "Show processing state",
    icon: "⏳",
    targetState: "processing",
    needsSessionData: true,
  },
  {
    id: "review-ready",
    name: "Review Ready",
    description: "Show review with summary",
    icon: "✏️",
    targetState: "review_ready",
    needsSessionData: true,
    needsSummary: true,
  },
  {
    id: "recording",
    name: "Recording",
    description: "Show recorder modal",
    icon: "🎙️",
    targetState: "recording",
    needsSessionData: true,
  },
  {
    id: "templates",
    name: "Templates",
    description: "Show template selection",
    icon: "📋",
    targetState: "form_template_selection",
    needsTemplates: true,
  },
];

// Collapsible sections
const showTestConfig = ref(false);
const showMockData = ref(true);

/**
 * Create mock session data for testing
 */
const createMockSessionData = () => ({
  sessionId: mockData.sessionId,
  recorderSessionId: mockData.recorderSessionId,
  licenseId: mockData.licenseId,
  patientId: mockData.patientId,
  recorderUrl: mockData.recorderUrl,
});

/**
 * Transition to a specific state using the event-driven state machine.
 * This simulates the actual flow by sending appropriate events.
 */
const transitionToState = (newState: AIAssistantState) => {
  const currentState = machine.state;

  // First reset if needed to get to a clean state
  if (currentState !== "idle" && newState !== currentState) {
    // Save real templates before reset (reset clears them)
    const cachedTemplates = [...machine.templates];
    machine.reset();
    // Restore real templates if they existed
    if (cachedTemplates.length > 0) {
      machine.setTemplates(cachedTemplates);
    }
  }

  // Set up templates only if none are cached from the API
  if (machine.templates.length === 0 && mockData.templateCount > 0) {
    machine.setTemplates(generateMockTemplates(mockData.templateCount));
  }

  // Use events to transition through the state machine properly
  switch (newState) {
    case "idle":
      // Already reset above, nothing more needed
      break;

    case "form_template_selection":
      machine.send({ type: "FAB_CLICK" });
      break;

    case "device_selection":
      // Must go through template selection first
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      break;

    case "recording":
      // Go through template → device → recording
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      break;

    case "processing":
      // Go through template → device → recording → processing
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      machine.send({ type: "WS_RECORDED" });
      break;

    case "review_ready":
      // Full flow: template → device → recording → processing → review_ready
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      machine.send({ type: "WS_RECORDED" });
      machine.send({
        type: "WS_SUMMARIZED",
        summaryData: mockData.summaryData,
        transcriptionData: "Mock transcription data",
      });
      break;

    case "error":
      // Go to processing then trigger error
      machine.send({ type: "FAB_CLICK" });
      machine.send({
        type: "SESSION_CREATED",
        session: createMockSessionData(),
      });
      machine.send({
        type: "DEVICE_DESKTOP",
        recorderUrl: mockData.recorderUrl,
      });
      machine.send({ type: "WS_RECORDED" });
      machine.send({
        type: "WS_ERROR",
        error: "Simulated error for testing",
      });
      break;
  }
};

// Apply mock data to current session
const applyMockData = () => {
  machine.updateSessionData({
    sessionId: mockData.sessionId,
    recorderSessionId: mockData.recorderSessionId,
    licenseId: mockData.licenseId,
    patientId: mockData.patientId,
    summaryData: mockData.summaryData,
  });
  if (mockData.templateCount > 0) {
    machine.setTemplates(generateMockTemplates(mockData.templateCount));
  }
};

// Reset mock data to defaults
const resetMockData = () => {
  mockData.sessionId = "dev-session-" + Date.now().toString(36);
  mockData.recorderSessionId = "rec-" + Date.now().toString(36);
  mockData.recorderUrl = "https://recorder.noa.ai/dev-test";
  mockData.licenseId = "123";
  mockData.patientId = "456";
  mockData.templateCount = 3;
};

// Apply a screen preset
const applyScreenPreset = (preset: ScreenPreset) => {
  // Reset first
  machine.reset();

  // Set up templates if needed
  if (preset.needsTemplates || mockData.templateCount > 0) {
    machine.setTemplates(generateMockTemplates(mockData.templateCount || 3));
  }

  // Transition to the target state
  transitionToState(preset.targetState);
};

// Reset AI state machine
const resetAIState = () => {
  machine.reset();
};

const presets: Preset[] = [
  {
    id: "clean",
    name: "Clean Slate",
    description: "All simulations off",
    icon: "✨",
    settings: {
      simulateSocketDisconnect: false,
      simulatePollingTimeout: false,
    },
  },
  {
    id: "recovery",
    name: "Recovery Flow",
    description: "Test socket recovery",
    icon: "🔄",
    settings: {
      simulateSocketDisconnect: true,
      simulatePollingTimeout: false,
    },
  },
  {
    id: "timeout",
    name: "Timeout Test",
    description: "Test refresh fallback",
    icon: "⏱️",
    settings: {
      simulateSocketDisconnect: true,
      simulatePollingTimeout: true,
    },
  },
];

const isPresetActive = (preset: Preset) => {
  return (
    settings.value.simulateSocketDisconnect === preset.settings.simulateSocketDisconnect &&
    settings.value.simulatePollingTimeout === preset.settings.simulatePollingTimeout
  );
};

const applyPreset = (preset: Preset) => {
  updateSetting("simulateSocketDisconnect", preset.settings.simulateSocketDisconnect);
  updateSetting("simulatePollingTimeout", preset.settings.simulatePollingTimeout);
};

const toggleSetting = (key: "simulateSocketDisconnect" | "simulatePollingTimeout") => {
  updateSetting(key, !settings.value[key]);
};

const resetAllSettings = () => {
  resetSettings();
};

const clearStorageAndReload = () => {
  sessionStorage.clear();
  localStorage.removeItem("ai-assistant-dev-config");
  localStorage.removeItem("ai-assistant-dev-settings");
  window.location.reload();
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[13] || (_cache[13] = _createElementVNode("span", { class: "section-title" }, "Screen Transitions", -1)),
        _createElementVNode("span", {
          class: _normalizeClass(["current-state-badge", currentState.value])
        }, _toDisplayString(currentState.value), 3)
      ]),
      _createElementVNode("div", _hoisted_4, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(stateOptions, (stateOption) => {
          return _createElementVNode("button", {
            key: stateOption.id,
            class: _normalizeClass(["state-btn", { active: currentState.value === stateOption.id }]),
            onClick: ($event: any) => (transitionToState(stateOption.id))
          }, [
            _createElementVNode("span", _hoisted_6, _toDisplayString(stateOption.icon), 1),
            _createElementVNode("span", _hoisted_7, _toDisplayString(stateOption.label), 1)
          ], 10, _hoisted_5)
        }), 64))
      ]),
      (_unref(machine).sessionId)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _cache[14] || (_cache[14] = _createElementVNode("span", { class: "session-label" }, "Session:", -1)),
            _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(machine).sessionId), 1)
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_10, [
      _createElementVNode("div", {
        class: "section-header collapsible",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (showMockData.value = !showMockData.value))
      }, [
        _cache[16] || (_cache[16] = _createElementVNode("span", { class: "section-title" }, "Mock Data", -1)),
        (_openBlock(), _createElementBlock("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          width: "14",
          height: "14",
          class: _normalizeClass(["chevron", { open: showMockData.value }])
        }, [...(_cache[15] || (_cache[15] = [
          _createElementVNode("path", { d: "m9 18 6-6-6-6" }, null, -1)
        ]))], 2))
      ]),
      (showMockData.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createElementVNode("div", _hoisted_12, [
              _cache[17] || (_cache[17] = _createElementVNode("label", { class: "config-label" }, "Session ID", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "text",
                class: "config-input",
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((mockData.sessionId) = $event)),
                placeholder: "mock-session-123"
              }, null, 512), [
                [_vModelText, mockData.sessionId]
              ])
            ]),
            _createElementVNode("div", _hoisted_13, [
              _cache[18] || (_cache[18] = _createElementVNode("label", { class: "config-label" }, "Recorder Session", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "text",
                class: "config-input",
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((mockData.recorderSessionId) = $event)),
                placeholder: "rec-456"
              }, null, 512), [
                [_vModelText, mockData.recorderSessionId]
              ])
            ]),
            _createElementVNode("div", _hoisted_14, [
              _cache[19] || (_cache[19] = _createElementVNode("label", { class: "config-label" }, "Recorder URL", -1)),
              _withDirectives(_createElementVNode("input", {
                type: "text",
                class: "config-input",
                "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((mockData.recorderUrl) = $event)),
                placeholder: "https://recorder.example.com/..."
              }, null, 512), [
                [_vModelText, mockData.recorderUrl]
              ])
            ]),
            _createElementVNode("div", _hoisted_15, [
              _cache[20] || (_cache[20] = _createElementVNode("label", { class: "config-label" }, "Templates", -1)),
              _createElementVNode("div", _hoisted_16, [
                (_openBlock(), _createElementBlock(_Fragment, null, _renderList([0, 1, 3, 5], (count) => {
                  return _createElementVNode("button", {
                    key: count,
                    class: _normalizeClass(["config-option", { active: mockData.templateCount === count }]),
                    onClick: ($event: any) => (mockData.templateCount = count)
                  }, _toDisplayString(count), 11, _hoisted_17)
                }), 64))
              ])
            ]),
            _createElementVNode("div", { class: "mock-actions" }, [
              _createElementVNode("button", {
                class: "action-btn compact",
                onClick: applyMockData
              }, "Apply Mock Data"),
              _createElementVNode("button", {
                class: "action-btn compact",
                onClick: resetMockData
              }, "Reset")
            ])
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_18, [
      _cache[21] || (_cache[21] = _createElementVNode("div", { class: "section-header" }, [
        _createElementVNode("span", { class: "section-title" }, "Screen Presets")
      ], -1)),
      _createElementVNode("div", _hoisted_19, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(screenPresets, (preset) => {
          return _createElementVNode("button", {
            key: preset.id,
            class: "preset-card screen-preset",
            onClick: ($event: any) => (applyScreenPreset(preset))
          }, [
            _createElementVNode("span", _hoisted_21, _toDisplayString(preset.icon), 1),
            _createElementVNode("span", _hoisted_22, _toDisplayString(preset.name), 1),
            _createElementVNode("span", _hoisted_23, _toDisplayString(preset.description), 1)
          ], 8, _hoisted_20)
        }), 64))
      ])
    ]),
    _createElementVNode("div", _hoisted_24, [
      _cache[22] || (_cache[22] = _createElementVNode("div", { class: "section-header" }, [
        _createElementVNode("span", { class: "section-title" }, "Simulation Presets")
      ], -1)),
      _createElementVNode("div", _hoisted_25, [
        (_openBlock(), _createElementBlock(_Fragment, null, _renderList(presets, (preset) => {
          return _createElementVNode("button", {
            key: preset.id,
            class: _normalizeClass(["preset-card", { active: isPresetActive(preset) }]),
            onClick: ($event: any) => (applyPreset(preset))
          }, [
            _createElementVNode("span", _hoisted_27, _toDisplayString(preset.icon), 1),
            _createElementVNode("span", _hoisted_28, _toDisplayString(preset.name), 1),
            _createElementVNode("span", _hoisted_29, _toDisplayString(preset.description), 1)
          ], 10, _hoisted_26)
        }), 64))
      ])
    ]),
    _createElementVNode("div", _hoisted_30, [
      _cache[29] || (_cache[29] = _createElementVNode("div", { class: "section-header" }, [
        _createElementVNode("span", { class: "section-title" }, "Individual Controls")
      ], -1)),
      _createElementVNode("div", _hoisted_31, [
        _createElementVNode("div", {
          class: _normalizeClass(["toggle-item", { active: _unref(settings).simulateSocketDisconnect }])
        }, [
          _cache[24] || (_cache[24] = _createElementVNode("div", { class: "toggle-info" }, [
            _createElementVNode("span", { class: "toggle-name" }, "Socket Disconnect"),
            _createElementVNode("span", { class: "toggle-desc" }, "Disconnect socket 1s after \"recorded\" status")
          ], -1)),
          _createElementVNode("label", _hoisted_32, [
            _createElementVNode("input", {
              type: "checkbox",
              checked: _unref(settings).simulateSocketDisconnect,
              onChange: _cache[4] || (_cache[4] = ($event: any) => (toggleSetting('simulateSocketDisconnect')))
            }, null, 40, _hoisted_33),
            _cache[23] || (_cache[23] = _createElementVNode("span", { class: "slider" }, null, -1))
          ])
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["toggle-item", { active: _unref(settings).simulatePollingTimeout }])
        }, [
          _cache[26] || (_cache[26] = _createElementVNode("div", { class: "toggle-info" }, [
            _createElementVNode("span", { class: "toggle-name" }, "Polling Timeout"),
            _createElementVNode("span", { class: "toggle-desc" }, "Polling never succeeds, timeout reduced to 15s")
          ], -1)),
          _createElementVNode("label", _hoisted_34, [
            _createElementVNode("input", {
              type: "checkbox",
              checked: _unref(settings).simulatePollingTimeout,
              onChange: _cache[5] || (_cache[5] = ($event: any) => (toggleSetting('simulatePollingTimeout')))
            }, null, 40, _hoisted_35),
            _cache[25] || (_cache[25] = _createElementVNode("span", { class: "slider" }, null, -1))
          ])
        ], 2),
        _createElementVNode("div", {
          class: _normalizeClass(["toggle-item", { active: _unref(testConfig).autoStart }])
        }, [
          _cache[28] || (_cache[28] = _createElementVNode("div", { class: "toggle-info" }, [
            _createElementVNode("span", { class: "toggle-name" }, "Auto Start"),
            _createElementVNode("span", { class: "toggle-desc" }, "Automatically open the popover on load")
          ], -1)),
          _createElementVNode("label", _hoisted_36, [
            _createElementVNode("input", {
              type: "checkbox",
              checked: _unref(testConfig).autoStart,
              onChange: _cache[6] || (_cache[6] = ($event: any) => (_unref(updateConfig)('autoStart', !_unref(testConfig).autoStart)))
            }, null, 40, _hoisted_37),
            _cache[27] || (_cache[27] = _createElementVNode("span", { class: "slider" }, null, -1))
          ])
        ], 2)
      ])
    ]),
    _createElementVNode("div", _hoisted_38, [
      _createElementVNode("div", {
        class: "section-header collapsible",
        onClick: _cache[7] || (_cache[7] = ($event: any) => (showTestConfig.value = !showTestConfig.value))
      }, [
        _cache[31] || (_cache[31] = _createElementVNode("span", { class: "section-title" }, "Test Configuration", -1)),
        (_openBlock(), _createElementBlock("svg", {
          viewBox: "0 0 24 24",
          fill: "none",
          stroke: "currentColor",
          "stroke-width": "2",
          width: "14",
          height: "14",
          class: _normalizeClass(["chevron", { open: showTestConfig.value }])
        }, [...(_cache[30] || (_cache[30] = [
          _createElementVNode("path", { d: "m9 18 6-6-6-6" }, null, -1)
        ]))], 2))
      ]),
      (showTestConfig.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_39, [
            _createElementVNode("div", _hoisted_40, [
              _cache[32] || (_cache[32] = _createElementVNode("label", { class: "config-label" }, "Position", -1)),
              _createElementVNode("div", _hoisted_41, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(POSITION_OPTIONS), (opt) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: opt.value,
                    class: _normalizeClass(["config-option", { active: _unref(testConfig).position === opt.value }]),
                    onClick: ($event: any) => (_unref(updateConfig)('position', opt.value))
                  }, _toDisplayString(opt.label), 11, _hoisted_42))
                }), 128))
              ])
            ]),
            _createElementVNode("div", _hoisted_43, [
              _cache[33] || (_cache[33] = _createElementVNode("label", { class: "config-label" }, "Token", -1)),
              _createElementVNode("input", {
                type: "text",
                class: "config-input token-input",
                value: _unref(testConfig).token,
                onInput: _cache[8] || (_cache[8] = ($event: any) => (_unref(updateConfig)('token', ($event.target as HTMLInputElement).value))),
                placeholder: "JWT Token"
              }, null, 40, _hoisted_44)
            ]),
            _createElementVNode("div", _hoisted_45, [
              _cache[34] || (_cache[34] = _createElementVNode("label", { class: "config-label" }, "User ID", -1)),
              _createElementVNode("input", {
                type: "text",
                class: "config-input",
                value: _unref(testConfig).userId,
                onInput: _cache[9] || (_cache[9] = ($event: any) => (_unref(updateConfig)('userId', ($event.target as HTMLInputElement).value)))
              }, null, 40, _hoisted_46)
            ]),
            _createElementVNode("div", _hoisted_47, [
              _cache[35] || (_cache[35] = _createElementVNode("label", { class: "config-label" }, "License ID", -1)),
              _createElementVNode("input", {
                type: "text",
                class: "config-input",
                value: _unref(testConfig).licenseId,
                onInput: _cache[10] || (_cache[10] = ($event: any) => (_unref(updateConfig)('licenseId', ($event.target as HTMLInputElement).value)))
              }, null, 40, _hoisted_48)
            ]),
            _createElementVNode("div", _hoisted_49, [
              _cache[36] || (_cache[36] = _createElementVNode("label", { class: "config-label" }, "Patient ID", -1)),
              _createElementVNode("input", {
                type: "text",
                class: "config-input",
                value: _unref(testConfig).patientId,
                onInput: _cache[11] || (_cache[11] = ($event: any) => (_unref(updateConfig)('patientId', ($event.target as HTMLInputElement).value)))
              }, null, 40, _hoisted_50)
            ]),
            _createElementVNode("div", _hoisted_51, [
              _cache[37] || (_cache[37] = _createElementVNode("label", { class: "config-label" }, "PMS", -1)),
              _createElementVNode("div", _hoisted_52, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(PMS_OPTIONS), (opt) => {
                  return (_openBlock(), _createElementBlock("button", {
                    key: opt.value,
                    class: _normalizeClass(["config-option", { active: _unref(testConfig).pms === opt.value }]),
                    onClick: ($event: any) => (_unref(updateConfig)('pms', opt.value))
                  }, _toDisplayString(opt.label), 11, _hoisted_53))
                }), 128))
              ])
            ]),
            _createElementVNode("button", {
              class: "action-btn compact",
              onClick: _cache[12] || (_cache[12] = 
//@ts-ignore
(...args) => (_unref(resetTestConfig) && _unref(resetTestConfig)(...args)))
            }, "Reset Config")
          ]))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", { class: "section" }, [
      _cache[41] || (_cache[41] = _createElementVNode("div", { class: "section-header" }, [
        _createElementVNode("span", { class: "section-title" }, "Actions")
      ], -1)),
      _createElementVNode("div", { class: "actions-list" }, [
        _createElementVNode("button", {
          class: "action-btn",
          onClick: resetAllSettings
        }, [...(_cache[38] || (_cache[38] = [
          _createElementVNode("svg", {
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "2",
            width: "14",
            height: "14"
          }, [
            _createElementVNode("path", { d: "M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" }),
            _createElementVNode("path", { d: "M3 3v5h5" })
          ], -1),
          _createTextVNode(" Reset All Settings ", -1)
        ]))]),
        _createElementVNode("button", {
          class: "action-btn",
          onClick: resetAIState
        }, [...(_cache[39] || (_cache[39] = [
          _createElementVNode("svg", {
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "2",
            width: "14",
            height: "14"
          }, [
            _createElementVNode("circle", {
              cx: "12",
              cy: "12",
              r: "10"
            }),
            _createElementVNode("path", { d: "m15 9-6 6" }),
            _createElementVNode("path", { d: "m9 9 6 6" })
          ], -1),
          _createTextVNode(" Reset AI State ", -1)
        ]))]),
        _createElementVNode("button", {
          class: "action-btn danger",
          onClick: clearStorageAndReload
        }, [...(_cache[40] || (_cache[40] = [
          _createElementVNode("svg", {
            viewBox: "0 0 24 24",
            fill: "none",
            stroke: "currentColor",
            "stroke-width": "2",
            width: "14",
            height: "14"
          }, [
            _createElementVNode("path", { d: "M3 6h18" }),
            _createElementVNode("path", { d: "M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" }),
            _createElementVNode("path", { d: "M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" })
          ], -1),
          _createTextVNode(" Clear Storage & Reload ", -1)
        ]))])
      ])
    ])
  ]))
}
}

})